import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Eye, Pencil, Trash2 } from 'lucide-react';
import SectionHeader from './SectionHeader';

const CareerPlansSection = ({ 
  careerPlans, 
  sectionOrder, 
  onMoveSectionUp, 
  onMoveSectionDown, 
  onDeleteCareerPlan,
  isVisible 
}) => {
  const navigate = useNavigate();

  if (!isVisible || !careerPlans || careerPlans.length === 0) return null;

  return (
    <div className="mb-8 sm:mb-12">
      <SectionHeader 
        title="Your Career Plans" 
        sectionId="careerPlans"
        viewAllPath="/career-plan-hub"
        viewAllColor="purple"
        sectionOrder={sectionOrder}
        onMoveSectionUp={onMoveSectionUp}
        onMoveSectionDown={onMoveSectionDown}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {careerPlans.slice(0, 3).map((plan) => (
          <div
            key={plan.id}
            className="p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-sky-600 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base mb-1 line-clamp-1">
              {plan.request_data?.short_term_goal || 'Career Plan'}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 line-clamp-1">
              {plan.request_data?.career_status || 'Career Development'}
            </p>
            <p className="text-xs text-gray-500 mb-2 sm:mb-3">
              Created {new Date(plan.created_at).toLocaleDateString()}
            </p>
            <div className="mb-3 flex items-center space-x-2 sm:space-x-4 text-xs text-gray-500">
              <span>{plan.plan_data?.career_pathways?.length || 0} pathways</span>
              <span>•</span>
              <span>{plan.plan_data?.skill_gaps?.missing_skills?.length || 0} skills</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/career-plan-view/${plan.id}`);
                }}
                variant="outline"
                className="border-purple-200 text-purple-600 hover:bg-purple-50 text-xs px-2 sm:px-3 min-h-[36px]"
              >
                <Eye className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">View</span>
              </Button>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/career-planner?edit=${plan.id}`);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 sm:px-3 min-h-[36px]"
              >
                <Pencil className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteCareerPlan(plan.id);
                }}
                className="border-red-200 text-red-600 hover:bg-red-50 text-xs px-2 sm:px-3 min-h-[36px]"
              >
                <Trash2 className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CareerPlansSection;
